/**
 * useMaterialThumbnails - Hook that provides blob URLs for material texture thumbnails.
 *
 * Loads the low-resolution albedo binary (128×128 per material, ~3.4 MB total),
 * extracts each material's layer into a canvas, converts to blob URLs, and caches
 * them for the lifetime of the page.
 *
 * NATIVE SIZE, NOT 48. The source layers ARE 128×128, so downscaling threw away detail that both
 * callers then asked for again: the build menu draws these at 72–88 px (upscaling a 48 px thumbnail,
 * which is why they read soft), and the world dialog's material grid draws them at ~110 px. Rendering
 * at the source size also removes the intermediate canvas — one putImageData instead of putImageData
 * plus a drawImage. Costs roughly 1–2 MB of blobs for 52 materials, held once for the page.
 *
 * Returns `null` while loading, then a `string[]` of blob URLs indexed by material ID.
 */

import { useState, useEffect } from 'react';
import { MATERIAL_PALLET } from '@worldify/shared';
import { textureCache } from '../game/material/TextureCache';

// Module-level cache so we only generate thumbnails once across all mounts
let cachedThumbnails: string[] | null = null;
let loadPromise: Promise<string[]> | null = null;

// Set from the pallet's own layer size below — the point is that it is NOT a resize.

async function generateThumbnails(): Promise<string[]> {
  const pallet = MATERIAL_PALLET as { maps: { low: { albedo: { width: number; height: number; channels: string; layers: number } } } };
  const meta = pallet.maps.low.albedo;
  const { width, height, channels } = meta;
  const channelCount = channels.length; // 4 for RGBA

  // Try loading from IndexedDB cache first, then fetch from CDN
  let data: Uint8Array;
  const cached = await textureCache.getTexture('low', 'albedo');
  if (cached) {
    data = new Uint8Array(cached);
  } else {
    const baseUrl = await textureCache.getLatestMaterialUrl();
    const response = await fetch(`${baseUrl}/low/albedo.bin`);
    if (!response.ok) throw new Error(`Failed to fetch albedo.bin: ${response.status}`);
    const buffer = await response.arrayBuffer();
    data = new Uint8Array(buffer);
  }

  const layerSize = width * height * channelCount;
  const materialCount = data.byteLength / layerSize;

  // One canvas at the source's own resolution — no resize step at all.
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  const urls: string[] = [];

  for (let i = 0; i < materialCount; i++) {
    const offset = i * layerSize;
    const imageData = ctx.createImageData(width, height);

    for (let p = 0; p < width * height; p++) {
      const srcIdx = offset + p * channelCount;
      const dstIdx = p * 4;
      imageData.data[dstIdx] = data[srcIdx];
      imageData.data[dstIdx + 1] = data[srcIdx + 1];
      imageData.data[dstIdx + 2] = data[srcIdx + 2];
      imageData.data[dstIdx + 3] = channelCount === 4 ? data[srcIdx + 3] : 255;
    }

    ctx.putImageData(imageData, 0, 0);

    // Convert to blob URL
    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((b) => resolve(b!), 'image/png');
    });
    urls.push(URL.createObjectURL(blob));
  }

  return urls;
}

export function useMaterialThumbnails(): string[] | null {
  const [thumbnails, setThumbnails] = useState<string[] | null>(cachedThumbnails);

  useEffect(() => {
    if (cachedThumbnails) {
      setThumbnails(cachedThumbnails);
      return;
    }

    if (!loadPromise) {
      loadPromise = generateThumbnails().then((urls) => {
        cachedThumbnails = urls;
        return urls;
      });
    }

    loadPromise.then(setThumbnails).catch((err) => {
      console.warn('[MaterialThumbnails] Failed to generate thumbnails:', err);
    });
  }, []);

  return thumbnails;
}
